import * as React from "react";
import { FormProvider, useForm, type UseFormSetError } from "react-hook-form";
import { toast } from "react-toastify";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FormInput } from "@/components/forms/input";

interface FormInputs {
  name: string;
}

interface Props {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  title: string;
  placeholder: string;
  initialName: string;
  successMessage: string;
  onRename: (name: string) => Promise<void>;
  onError: (error: unknown, setError: UseFormSetError<FormInputs>) => void;
}

export const RenameDialog: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  title,
  placeholder,
  initialName,
  successMessage,
  onRename,
  onError,
}) => {
  const methods = useForm<FormInputs>({
    defaultValues: {
      name: initialName,
    },
  });
  const { reset } = methods;

  React.useEffect(() => {
    if (isOpen) {
      reset({ name: initialName });
    }
  }, [isOpen, reset, initialName]);

  const onSubmit = async ({ name }: FormInputs) => {
    try {
      await onRename(name);
      setIsOpen(false);
      toast.success(successMessage);
    } catch (error) {
      onError(error, methods.setError);
    }
  };

  return (
    <FormProvider {...methods}>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent>
          <form method="post" onSubmit={methods.handleSubmit(onSubmit)}>
            <DialogHeader>
              <DialogTitle>{title}</DialogTitle>
            </DialogHeader>

            <FormInput name="name" placeholder={placeholder} className="my-3" />

            <DialogFooter className="mt-4">
              <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={methods.formState.isSubmitting}>
                {methods.formState.isSubmitting ? "Saving..." : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </FormProvider>
  );
};
